/**
 * Shared hook for the "infer market level" POST call.
 * Used by both candidate-intelligence and evaluation-settings corpus browser.
 *
 * Returns { inferMarketLevel, loading, error } where `error` is already a
 * PT-localised string (or null when clean).
 */

import type { MarketLevelResult } from "@shared/labs/candidate-assessment";
import { useState } from "react";
import { rpcClient } from "@/lib/rpc";

export interface UseInferMarketLevelResult {
  inferMarketLevel: (evaluationId: string) => Promise<MarketLevelResult | null>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

export function useInferMarketLevel(): UseInferMarketLevelResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function inferMarketLevel(evaluationId: string): Promise<MarketLevelResult | null> {
    setLoading(true);
    setError(null);
    try {
      const data = (await rpcClient.labs.evaluationsInferMarketLevel({ id: evaluationId })) as {
        evaluationId: string;
        result: MarketLevelResult;
      };
      return data.result;
    } catch (e: any) {
      const message = e?.message ?? "Erro de rede ao computar o nível de mercado.";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { inferMarketLevel, loading, error, clearError: () => setError(null) };
}
